/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Stack, TableCell, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import { usePermission } from "../../../hooks/usePermission";
import { selectRemoteConfig } from "../../../services/firebase/remote-config/remoteConfig.slice";
import TestIds from "../../../testIds";
import { formatPrice } from "../../../util/helpers";
import { selectCurrentFarmerAccount } from "../../azure-b2c/storage";
import { CartItem } from "../../cart/storage/cart.types";
import { EditButton } from "../../products/components/ProductCard/EditButton";
import { selectProduct } from "../../products/storage/stocks.slice";
import {
  Paddock,
  SEED_MIX_TYPE,
  SeedProductLine,
  SeedTypesenseProduct,
} from "../types/SeedOrderFormType";
import {
  roundedTo,
  roundedToWholeNumber,
  seedWeightForPaddockIgnoreIsMixing,
} from "../utils/calculateTotal";

import { StyledTableRow, StyledTableRowMobile, TableCellContent } from "./SeedTableCell";
import { useSeedEditPriceModal } from "./useSeedEditPriceModal";

export interface SeedOrderTableProductRowProps {
  paddock: Paddock;
  seedProduct: SeedProductLine;
  mixType?: SEED_MIX_TYPE;
}
export const SeedOrderTableProductRow = ({
  paddock,
  seedProduct,
  mixType,
}: SeedOrderTableProductRowProps) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const accountNumber = currentAccount?.accountNumber;
  const isTfo = currentAccount?.hasTfoAccess || false;
  const remoteConfig = useSelector(selectRemoteConfig);
  const canEditPrice = usePermission("StaffSpecific_Pricing_PriceEdit").allowed;

  const { setOpen, setItem, setAccountNumber } = useSeedEditPriceModal();

  const product = useSelector(selectProduct(seedProduct.productId));
  const productSku = product?.itemNumber ?? "";
  const productName = product?.name ?? "";
  const unitDiscountedPriceExcGst =
    (seedProduct?.discountedPriceExcGst ?? 0) / seedProduct.quantity;
  const price = seedProduct.editedPriceExcGst ?? unitDiscountedPriceExcGst ?? 0;
  const priceLabel = formatPrice(price);
  const rawQuantity = seedProduct.isMixingFee
    ? seedProduct.quantity
    : seedWeightForPaddockIgnoreIsMixing(seedProduct, paddock);
  const quantity = seedProduct.quantityRoundedUp ? roundedToWholeNumber(rawQuantity) : rawQuantity;
  const total = formatPrice(quantity * price);

  const unit = useMemo(() => {
    if (product && (product as unknown as SeedTypesenseProduct).unit) {
      return (product as unknown as SeedTypesenseProduct).unit;
    }
    return "kg";
  }, [product]);

  const onClick = () => {
    setItem({ ...seedProduct, name: product?.name } as unknown as CartItem);
    setOpen(true);
    setAccountNumber(accountNumber);
  };

  if (isSm) {
    return (
      <StyledTableRowMobile
        data-testid={TestIds.feat.seed.product.row.container}
        key={`seedProductRow-${seedProduct.productId}`}
        display="flex"
        flexDirection={"column"}
        p={2}
        gap={1}
      >
        <Typography variant="body2" fontWeight="bold">
          {productName}
        </Typography>
        <Typography variant="body2">{`SKU: ${productSku}`}</Typography>
        <Box display="flex" gap={1}>
          <Typography variant="body2" fontWeight="bold" color={theme.colors.primary500}>
            Unit price
          </Typography>
          <Typography variant="body2">{priceLabel}</Typography>
          {isTfo && remoteConfig.discountEnabled && (
            <EditButton onClick={onClick} testId={TestIds.feat.seed.product.row.editButton} />
          )}
        </Box>
        {mixType === SEED_MIX_TYPE.CUSTOM ? (
          <Box display="flex" gap={1}>
            <Typography variant="body2" fontWeight="bold" color={theme.colors.primary500}>
              Sowing rate
            </Typography>
            <Typography variant="body2">{`${roundedTo(seedProduct.sowingRate)} kg/Ha`}</Typography>
          </Box>
        ) : null}
        <Box display="flex" gap={1}>
          <Box flex={1} gap={1}>
            <Typography variant="body2" fontWeight="bold" color={theme.colors.primary500}>
              Quantity
            </Typography>
            <Typography variant="body2">{`${quantity} ${unit}`}</Typography>
          </Box>
          <Box flex={1} gap={1}>
            <Typography variant="body2" fontWeight="bold" color={theme.colors.primary500}>
              Total (GST excl.)
            </Typography>
            <Typography variant="body2">{total}</Typography>
          </Box>
        </Box>
      </StyledTableRowMobile>
    );
  }

  return (
    <StyledTableRow
      key={`seedProductRow-${seedProduct.productId}`}
      data-testid={TestIds.feat.seed.product.row.container}
    >
      <TableCell>
        <TableCellContent label={productSku} textAlign="start" />
      </TableCell>
      <TableCell>
        <TableCellContent label={productName} textAlign="start" />
      </TableCell>
      {mixType === SEED_MIX_TYPE.CUSTOM ? (
        <TableCell>
          {seedProduct.isMixingFee ? null : (
            <TableCellContent label={`${roundedTo(seedProduct.sowingRate)} kg/Ha`} />
          )}
        </TableCell>
      ) : null}
      <TableCell>
        <TableCellContent label={`${quantity} ${unit}`} />
      </TableCell>
      <TableCell>
        <Stack alignItems="flex-end">
          <TableCellContent label={priceLabel} />
          {isTfo && remoteConfig.discountEnabled && canEditPrice && (
            <EditButton testId={TestIds.feat.seed.product.row.editButton} onClick={onClick} />
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <TableCellContent label={total} />
      </TableCell>
    </StyledTableRow>
  );
};
